// src/pages/YearEndCloseCard.tsx
import React, { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { apiPost } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { Button } from "../components/Button";
import { formatMoney } from "../utils/formatNumber";
import { A } from "../styles/appleClasses";

type YearEndCloseResponse = {
  journal_entry_id: number;
  year: number;
  status: string;
  lines_posted: number;
  net_income: number;
  accounts: any[];
};

type DepreciationDueDetail = {
  code: string;
  message: string;
  closing_date: string;
  due_count: number;
  sample?: any[];
};

function money(n: number | undefined | null) {
  return typeof n === "number" && !Number.isNaN(n)
    ? formatMoney(n, "$", { minDp: 0, maxDp: 2 })
    : "—";
}

export default function YearEndCloseCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id?: string }>();

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const [year, setYear] = useState<number>(currentYear - 1);

  const [loading, setLoading] = useState(false);

  const [success, setSuccess] = useState<{
    year: number;
    journalEntryId: number;
    netIncome: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showDepreciationModal, setShowDepreciationModal] = useState(false);
  const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);
  const [depreciationWarning, setDepreciationWarning] =
    useState<DepreciationDueDetail | null>(null);

  function companyQuery() {
    return company_id ? `?company_id=${company_id}` : "";
  }

  function getErrorMessage(e: any) {
    const detail = e?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (detail?.message) return detail.message;
    return e?.message || String(e);
  }

  function handleCloseSuccess(resp: YearEndCloseResponse) {
    setSuccess({
      year: resp.year,
      journalEntryId: resp.journal_entry_id,
      netIncome: money(resp.net_income),
    });
  }

  const openCloseConfirmation = () => {
    setError(null);
    setSuccess(null);
    setShowDepreciationModal(false);
    setShowConfirmCloseModal(true);
  };

  const executeCloseYear = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const resp = await apiPost<YearEndCloseResponse>(
        `/year-end/close/${year}${companyQuery()}`,
        {},
      );

      setShowConfirmCloseModal(false);
      handleCloseSuccess(resp);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;

      const detailText =
        typeof detail === "string"
          ? detail
          : String(detail?.message || detail?.code || "");

      if (
        detail?.code === "DEPRECIATION_DUE" ||
        detailText.toLowerCase().includes("unrecorded depreciation")
      ) {
        setShowConfirmCloseModal(false);
        setDepreciationWarning(
          typeof detail === "object"
            ? detail
            : {
                code: "DEPRECIATION_DUE",
                message: detailText,
                closing_date: `${year}-12-31`,
                due_count: 0,
              },
        );
        setShowDepreciationModal(true);
        return;
      }

      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const labelCls = `block ${A.label} mb-1`;

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition";

  const headerShell =
    "sticky top-0 z-30 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70 border-b border-gray-100";

  const headerInner =
    "w-full max-w-3xl mx-auto px-2 sm:px-6 pt-4 pb-2 mb-2 flex items-center";

  const dangerOutline =
    "border border-red-200 text-red-700 hover:bg-red-50 focus:ring-red-200";

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      <div className={headerShell}>
        <div className={headerInner}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={iconBtn}
            aria-label={t("directory.common.back")}
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
          </button>

          <div className="flex-1 flex justify-center pointer-events-none">
            <h2 className={`${A.title} text-center`}>{t("books.yearEndClosing")}</h2>
          </div>

          <button
            type="button"
            onClick={() => navigate(companyRoute(company_id, "/dashboard"))}
            className={iconBtn}
            aria-label={t("directory.common.close")}
          >
            <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
          </button>
        </div>
      </div>

      <div className="w-full max-w-3xl mx-auto px-2 sm:px-6 pb-10">
        <div className={`${A.card} p-5 md:p-6`}>
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <div className={A.title}>{t("books.yearEndClosing")}</div>
              <div className="text-xs text-red-600 font-semibold mt-0.5">
                {t("books.advanced")}{" • "}{t("yearEndClose.oneWayAction")}
              </div>
            </div>
          </div>

          <div className={`${A.body} leading-relaxed`}>
            <Trans
              i18nKey="yearEndClose.irreversibleWarning"
              components={{
                action: <span className="font-semibold text-red-600" />,
                irreversible: <span className="font-semibold text-red-600" />,
              }}
            />
          </div>

          <div className="mt-5 flex flex-col md:flex-row md:items-end gap-3">
            <div className="w-full md:w-[160px]">
              <label className={labelCls}>{t("yearEndClose.year")}</label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                min={2000}
                max={2100}
                className={A.inputBase}
              />
            </div>

            <div className="flex gap-3 items-center">
              <Button
                variant="secondary"
                minWidth="140px"
                disabled={loading}
                onClick={openCloseConfirmation}
                className={dangerOutline}
              >
                {loading ? t("yearEndClose.closingDots") : t("yearEndClose.closeYear")}
              </Button>
            </div>
          </div>

          {success && (
            <div className="mt-4 text-[13px] font-medium text-emerald-700">
              {t("yearEndClose.success", {
                ...success,
                year: String(success.year),
                journalEntryId: String(success.journalEntryId),
              })}
            </div>
          )}

          {error && (
            <div className="mt-4 text-[13px] font-medium text-red-600">
              {error}
            </div>
          )}
        </div>
      </div>

      {showConfirmCloseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white border border-gray-200 shadow-md p-5">
            <div className={A.title}>{t("yearEndClose.confirmTitle")}</div>

            <div className="mt-4 space-y-3 text-[13px] leading-relaxed text-gray-700">
              <p>
                {t("yearEndClose.confirmExplanation", { year })}
              </p>
              <p className="font-semibold text-red-600">
                {t("yearEndClose.irreversibleConfirmation")}
              </p>
              <p>
                {t("yearEndClose.finalChecklist")}
              </p>
            </div>

            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button
                variant="secondary"
                disabled={loading}
                onClick={() => setShowConfirmCloseModal(false)}
              >
                {t("directory.common.cancel")}
              </Button>

              <Button
                variant="secondary"
                disabled={loading}
                onClick={executeCloseYear}
                className={dangerOutline}
              >
                {loading ? t("yearEndClose.closingDots") : t("yearEndClose.confirmCloseYear")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDepreciationModal && depreciationWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white border border-gray-200 shadow-md p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className={A.title}>{t("yearEndClose.unrecordedDepreciation")}</div>
                <div className="mt-1 text-xs text-gray-500">
                  {depreciationWarning.due_count === 1
                    ? t("yearEndClose.dueSchedules_one", { replace: { count: depreciationWarning.due_count ?? "" } })
                    : t("yearEndClose.dueSchedules_other", { replace: { count: depreciationWarning.due_count ?? "" } })}
                </div>
              </div>

              <button
                type="button"
                className={iconBtn}
                onClick={() => setShowDepreciationModal(false)}
                aria-label={t("yearEndClose.closeDepreciationWarning")}
              >
                <XMarkIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="mt-4 space-y-3 text-[13px] leading-relaxed text-gray-700">
              <p>
                {t("yearEndClose.depreciationExplanation")}
              </p>
              <p>
                {t("yearEndClose.depreciationInstruction")}
              </p>
              {depreciationWarning.closing_date && (
                <p className="font-medium">
                  {t("yearEndClose.requiredThrough", { date: depreciationWarning.closing_date })}
                </p>
              )}
            </div>

            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowDepreciationModal(false)}
              >
                {t("directory.common.cancel")}
              </Button>

              <Button
                variant="primary"
                onClick={() =>
                  navigate(companyRoute(company_id, "/spending/fixed-assets"))
                }
              >
                {t("yearEndClose.goToFixedAssets")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
